import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { AuthUser, LoginRequest } from '@/types';
import { authService } from '@/services/authService';

interface AuthState {
   user: AuthUser | null;
   loading: boolean;
   login: (credentials: LoginRequest) => Promise<AuthUser>;
   logout: () => Promise<void>;
   setUser: (user: AuthUser | null) => void;
   setLoading: (loading: boolean) => void;
}

const useAuthStore = create<AuthState>((set) => ({
   user: null,
   loading: true,
   login: async (credentials: LoginRequest) => {
      try {
         set({ loading: true });
         const { user } = await authService.login(credentials);
         set({ user, loading: false });
         return user;
      } catch (error: any) {
         set({ loading: false });
         throw error;
      }
   },
   logout: async () => {
      try {
         await authService.logout();
         set({ user: null });
      } catch (error: any) {
         // Even if logout API fails, clear the user state
         set({ user: null });
         throw error;
      }
   },
   setUser: (user) => set({ user }),
   setLoading: (loading) => set({ loading }),
}));

export const useAuth = () => {
   const authStore = useAuthStore();
   const [isInitialized, setIsInitialized] = useState(false);

   useEffect(() => {
      const initializeAuth = async () => {
         try {
            // Check if user is already authenticated
            if (authService.isAuthenticated()) {
               console.log('🔍 Token found, verifying with server...');
               // Get current user from API
               const user = await authService.getCurrentUser();
               console.log('✅ User authenticated:', user.email);
               authStore.setUser(user);
            } else {
               console.log('❌ No token found, user not authenticated');
               // No token found, user is not authenticated
               authStore.setUser(null);
            }
         } catch (error: any) {
            console.error('❌ Failed to initialize auth:', error);
            console.error('Error details:', error.response?.data || error.message);

            // Only clear token if it's actually invalid (401/403), not for network errors
            if (error.response?.status === 401 || error.response?.status === 403) {
               console.log('🗑️ Clearing invalid token');
               authService.clearToken();
            } else {
               console.log('🔄 Keeping token, might be network issue');
            }

            authStore.setUser(null);
         } finally {
            authStore.setLoading(false);
            setIsInitialized(true);
         }
      };

      initializeAuth();
   }, []); // Empty dependency array - only run once

   return {
      ...authStore,
      loading: authStore.loading || !isInitialized,
   };
}; 